import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { useRouter } from "next/router";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Toggle } from "react-powerplug";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import RemoveInvitee from "~/lib/mutations/RemoveInvitee";
import TransferOwnership from "~/lib/mutations/TransferOwnership";
import {
  IRemoveInviteeMutation,
  IRemoveInviteeMutationVariables,
} from "~/lib/mutations/__generated__/RemoveInvitee.generated";
import {
  ITransferOwnershipMutation,
  ITransferOwnershipMutationVariables,
} from "~/lib/mutations/__generated__/TransferOwnership.generated";
import { ISelfieQuery } from "~/lib/queries/__generated__/SelfieQuery.generated";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import { IVendorUser } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import TransferIcon from "~/components/icons/TransferIcon";
import InviteModal from "~/components/invite/InviteModal";
import mediaQueries from "~/components/mediaQueries";
import Tooltip from "~/components/Tooltip";

interface IVendorTeamProps {
  vendor: NonNullable<IVendorQuery["company"]>;
}
export default function VendorTeam({ vendor }: IVendorTeamProps) {
  const loggedInUser = useSelfie();
  const intl = useIntl();
  const router = useRouter();

  if (!loggedInUser) {
    return null;
  }

  const userHasOwnershipRights = loggedInUser.role === "COMPANY_OWNER" || vendor.usersCanAccessAccountPage;
  const userCanTransferOwnership = loggedInUser.role === "COMPANY_OWNER";

  return (
    <div
      css={{
        borderRadius: 4,
        border: `1px solid ${palette.lightestGray}`,
        background: "#fff",
      }}
    >
      <div
        css={{
          padding: 16,

          borderBottom: `1px solid ${palette.lightestGray}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          css={{
            fontSize: 24,
            fontWeight: 600,
            color: palette.text,
          }}
        >
          <FormattedMessage defaultMessage="Team" id="wsUmh9" />
        </div>

        {userHasOwnershipRights && (
          <Toggle>
            {({ on: isOpen, toggle }) => (
              <>
                <span
                  role="button"
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Invite your team', id: '6g+RkT',
                  })}
                  css={{
                    marginLeft: "auto",
                    fontWeight: 600,
                    fontSize: 14,
                    color: palette.primary,
                    cursor: "pointer",
                  }}
                  onClick={toggle}
                >
                  <FormattedMessage defaultMessage="Invite Others" id="OHauMK" />
                </span>

                <InviteModal
                  title={intl.formatMessage({
                    defaultMessage: 'Invite Team Members', id: 'LeF5J5',
                  })}
                  subtitle={
                    <span>
                      {intl.formatMessage({
                        defaultMessage: 'They’ll be granted access to your Vendor profile', id: 'Y+CAMf',
                      })}
                    </span>
                  }
                  redirectTo={`/vendors/${vendor.slug}`}
                  targetType="VENDOR"
                  targetId={vendor._id}
                  isOpen={isOpen}
                  onRequestClose={toggle}
                />
              </>
            )}
          </Toggle>
        )}
      </div>
      <div
        css={{
          padding: 16,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gridGap: 16,
        }}
      >
        {(vendor.members || []).map((member) => (
          <div
            key={member._id}
            css={{
              padding: 12,
              borderRadius: 4,
              border: `1px solid ${palette.lightestGray}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
            }}
          >
            {member.role === "COMPANY_OWNER" && (
              <div
                css={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <span
                  css={mediaQueries({
                    fontWeight: 600,
                    background: palette.lightestBlue,
                    color: palette.innovatorBlue,
                    borderRadius: 12,
                    padding: 4,
                    fontSize: 10,
                    letterSpacing: 0.5,
                    display: "flex",
                    alignItems: "center",
                    textTransform: "uppercase",
                  })}
                >
                  {intl.formatMessage({
                    defaultMessage: 'Owner', id: 'zINlao',
                  })}
                </span>
              </div>
            )}

            <div
              css={{
                position: "absolute",
                top: 4,
                right: 4,
                display: "flex",
                flexDirection: "row",
              }}
            >
              {userCanTransferOwnership && (
                <Mutation<ITransferOwnershipMutation, ITransferOwnershipMutationVariables>
                  mutation={TransferOwnership}
                  refetchQueries={refetchQueries(["Selfie", "Vendor"])}
                >
                  {(transferOwnership) => (
                    <Tooltip
                      backgroundColor={palette.innovatorBlue}
                      borderColor={palette.innovatorBlue}
                      boxStyle={{
                        padding: 8,
                      }}
                      render={() => (
                        <span
                          css={{
                            color: palette.white,
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          <FormattedMessage defaultMessage="Transfer Ownership" id="d2dgcX" />
                        </span>
                      )}
                    >
                      {({ innerRef, bind }) => (
                        <span
                          ref={innerRef}
                          {...bind}
                          onClick={() => {
                            if (
                              window.confirm(
                                intl.formatMessage(
                                  {
                                    defaultMessage: 'Are you sure you want to transfer this vendor ownership to {fullName}?', id: '4KjHfq',
                                  },
                                  {
                                    fullName: member.fullName,
                                  },
                                ),
                              )
                            ) {
                              transferOwnership({
                                variables: {
                                  vendorId: vendor._id,
                                  newOwnerId: member._id,
                                },
                              });
                            }
                          }}
                          role="button"
                          css={{
                            cursor: "pointer",
                            borderRadius: 12,
                            padding: 4,
                            fontSize: 10,
                            letterSpacing: 0.5,
                          }}
                        >
                          <TransferIcon width={20} height={20} fill={palette.innovatorBlue} />
                        </span>
                      )}
                    </Tooltip>
                  )}
                </Mutation>
              )}
            </div>

            <div
              css={{
                position: "relative",
              }}
            >
              <Mutation<IRemoveInviteeMutation, IRemoveInviteeMutationVariables>
                mutation={RemoveInvitee}
                refetchQueries={refetchQueries(["Selfie", "Vendor", "CompanyAccountBillingInfo"])}
              >
                {(removeInvitee) => (
                  <Tooltip
                    backgroundColor={palette.red}
                    borderColor={palette.red}
                    render={() => (
                      <div
                        css={{
                          padding: 6,
                          fontWeight: 600,
                          color: palette.white,
                          fontSize: 14,
                        }}
                      >
                        <FormattedMessage defaultMessage="Remove Member" id="qhbBD7" />
                      </div>
                    )}
                  >
                    {({ bind, innerRef }) => (
                      <div>
                        <div
                          css={{
                            position: "relative",
                          }}
                        >
                          <ProfilePicture
                            image={member.avatar}
                            name={member.fullName}
                            size={60}
                            css={{
                              position: "static",
                            }}
                          />

                          {canRemoveMember(userHasOwnershipRights, loggedInUser, member) && (
                            <div
                              ref={innerRef}
                              role="button"
                              {...bind}
                              css={{
                                cursor: "pointer",
                                position: "absolute",
                                top: -5,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "100%",
                                right: -5,
                                width: 22,
                                height: 22,
                                background: palette.red,
                              }}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    loggedInUser && loggedInUser._id === member._id
                                      ? intl.formatMessage({
                                          defaultMessage: 'Do you want to leave this vendor?', id: 'cOr0z9',
                                        })
                                      : intl.formatMessage({
                                          defaultMessage: 'Do you want to remove this team member?', id: 'zvf/vl',
                                        }),
                                  )
                                ) {
                                  removeInvitee({
                                    variables: {
                                      targetId: vendor._id,
                                      inviteeId: member._id,
                                      targetType: "VENDOR",
                                    },
                                  }).then(() => {
                                    if (loggedInUser && loggedInUser._id === member._id) {
                                      router.push("/vendors/[vendorSlug]", `/vendors/${vendor.slug}`);
                                    }
                                  });
                                }
                              }}
                            >
                              <svg width={10} height={10}>
                                <path
                                  fill="#FFF"
                                  d="M6.188 5l3.566-3.565A.84.84 0 1 0 8.565.246L5 3.812 1.435.246A.84.84 0 1 0 .246 1.435L3.812 5 .246 8.565a.84.84 0 0 0 1.189 1.189L5 6.188l3.565 3.566a.84.84 0 1 0 1.189-1.189L6.188 5z"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Tooltip>
                )}
              </Mutation>
            </div>

            <div
              css={{
                marginTop: 8,
                textAlign: "center",
              }}
            >
              <p
                css={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 500,
                  color: palette.darkGray,
                }}
              >
                {member.fullName}
              </p>
              <p
                css={{
                  margin: "4px 0 0 0",
                  fontSize: 14,
                  color: palette.mediumGray,
                }}
              >
                {member.jobTitle}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function canRemoveMember(
  hasOwnershipRights: boolean,
  loggedInUser: NonNullable<ISelfieQuery["selfie"]>,
  member: {
    __typename: "VendorUser";
  } & Pick<IVendorUser, "_id" | "slug" | "role" | "firstName" | "fullName" | "jobTitle" | "avatar">,
) {
  if (hasOwnershipRights && loggedInUser._id !== member._id && member.role !== "COMPANY_OWNER") {
    return true;
  }

  if (loggedInUser._id === member._id && loggedInUser.role !== "COMPANY_OWNER") {
    return true;
  }

  return false;
}
